import type { CSSProperties, PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { layout } from '@visious/tokens';
import { IconButton, Menu, VolumeSlider, cx } from '../../ui';
import {
  barKnobColor,
  barRange,
  barSegments,
  barSponsorMarks,
  scrubHover,
  type MenuId,
  type PlayerMenu,
  type SponsorBarMark,
  type TimelineSegment,
} from './model';
import { SKIP_SEC, type WatchBinding } from './useWatchScreen';
import styles from './chrome.module.css';
import { useCopy } from '../../strings/useCopy';

/**
 * §7's chrome, rendering props. No core imports (non-negotiable 2), no URL building, no decisions:
 * every number below arrived computed, and every gesture is handed to the binding as raw coordinates
 * for core to interpret.
 *
 * It lives in its own file rather than in `view.tsx` because it is the only part of the page that is
 * *inside* the fullscreen element — which is also why the bar overlays the picture instead of sitting
 * under it. In fullscreen there is nowhere else for it to be.
 *
 * Its buttons moved onto the `media` variant of the shared primitive. That variant exists for
 * exactly this surface: everything here sits on an arbitrary video frame, so it takes `on-media` and a
 * translucent hover rather than any themed surface colour.
 */

/**
 * The width the storyboard tile is drawn at. From the token package because **core** crops the tile to
 * the same number (`storyboardPreview`), and a stylesheet and an arithmetic function disagreeing about
 * it is a preview that is scaled wrong with nothing to point at.
 */
const PREVIEW_WIDTH_PX = layout.previewWidth.n;

export function PlayIcon({ playing }: { playing: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      {playing ? (
        <path fill="currentColor" d="M6 4.5h4.2v15H6zM13.8 4.5H18v15h-4.2z" />
      ) : (
        <path fill="currentColor" d="M7 4.5 19.5 12 7 19.5z" />
      )}
    </svg>
  );
}

export function MuteIcon({ muted, volume }: { muted: boolean; volume: number }) {
  return (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
      <path fill="currentColor" d="M4 9h3.5L12 4.8v14.4L7.5 15H4z" />
      {muted ? (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          d="M15.5 9.5 20.5 14.5M20.5 9.5 15.5 14.5"
        />
      ) : (
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          // The second arc only appears above half volume, so the icon says how loud it is as well
          // as whether it is on.
          d={volume > 0.5 ? 'M15.8 9a4.2 4.2 0 0 1 0 6M18.4 6.6a7.8 7.8 0 0 1 0 10.8' : 'M15.8 9a4.2 4.2 0 0 1 0 6'}
        />
      )}
    </svg>
  );
}

/**
 * The two skip controls. A **three-quarter** arc with the gap on the side it skips towards and
 * the arrowhead at the arc's end, pointing down and out at 45° — so the loop reads as continuing off
 * the frame in that direction rather than closing back on itself. The old glyph was a near-full circle
 * whose head sat on top pointing sideways, which named neither the direction nor the act; this one
 * mirrors exactly, so back and forward are the same drawing read two ways.
 *
 * Every number below is on the 24 grid at r = 7.5 around the centre: the arc runs clockwise from
 * (17.3, 17.3) three quarters of the way round to (17.3, 6.7), and the head's base is centred on that
 * end point so the two meet without a seam.
 *
 * Exported for the double-tap disc (`view.tsx`), which draws it larger over the picture — the gesture
 * and the button are the same act, so they are the same glyph.
 */
export function SkipIcon({ forward, size = 20 }: { forward: boolean; size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden="true" focusable="false">
      <g transform={forward ? undefined : 'translate(24 0) scale(-1 1)'}>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
          d="M17.3 17.3A7.5 7.5 0 1 1 17.3 6.7"
        />
        {/* Base 6.6 long, 5 out to the tip, the base's midpoint sitting exactly on the arc's end
            (17.3, 6.7) and its long axis along the tangent there. Grown from 5.2 × 4: at the
            20 px the bar draws this at, the smaller head read as a thickening of the stroke rather
            than as an arrowhead. */}
        <path fill="currentColor" d="M20.84 10.24 14.97 9.03 19.63 4.37z" />
      </g>
    </svg>
  );
}

export function TheaterIcon({ active }: { active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="6" width="18" height="12" rx="1.2" />
        {/* In theater the inner mark hugs the frame; out of it, a narrower picture with side gutters. */}
        {active ? <path d="M5.5 8.5h13v7h-13z" /> : <path d="M8.5 8.5h7v7h-7z" />}
      </g>
    </svg>
  );
}

/**
 * The widen control: the frame the picture is in, and what is in it. Letterboxed, it is the 16∶9
 * frame with a solid bar above and below the picture; widened, the frame itself is the short wide
 * box and the picture fills it edge to edge, which is why that state is one solid shape.
 *
 * Its neighbour `TheaterIcon` keeps a fixed frame with an outlined mark inside, so at the 20 px both
 * are drawn at the two buttons never read as the same glyph: this one changes the frame's shape and
 * fills it, that one changes what sits inside a frame that never moves.
 */
function WidenIcon({ active }: { active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      {active ? (
        <rect x="3" y="7.4" width="18" height="9.2" rx="1.4" fill="currentColor" />
      ) : (
        <>
          <rect
            x="3"
            y="6"
            width="18"
            height="12"
            rx="1.2"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.9"
          />
          <path fill="currentColor" d="M5.4 7.9h13.2v2.1H5.4zM5.4 14h13.2v2.1H5.4z" />
        </>
      )}
    </svg>
  );
}

function FullscreenIcon({ active }: { active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="1.9"
        strokeLinecap="round"
        strokeLinejoin="round"
        d={
          active
            ? 'M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5'
            : 'M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5'
        }
      />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Menus
// ---------------------------------------------------------------------------

/**
 * The four menus' trigger glyphs. On the bar each control shows its icon plus the shortest honest
 * value — "1080p", "1.5×", a language code, or nothing (subtitles off) — the `valueShort` decision
 * made in `model.ts`. Same hand as every other icon: 24-grid strokes at 1.8.
 */
function AudioLanguageIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <circle cx="12" cy="12" r="8.4" />
        <ellipse cx="12" cy="12" rx="3.8" ry="8.4" />
        <path d="M4 12h16" />
      </g>
    </svg>
  );
}

function SubtitlesIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="5.5" width="18" height="13" rx="1.8" />
        <path d="M6.5 12h6M15 12h2.5M6.5 15h2.5M11.5 15h6" />
      </g>
    </svg>
  );
}

function SpeedIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M4.5 16.5a8.5 8.5 0 1 1 15 0" />
        <path d="M12 15.5 15.8 9.7" />
      </g>
    </svg>
  );
}

function QualityIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true" focusable="false">
      <g fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
        <path d="M5.5 19v-4M12 19V9.5M18.5 19V4.5" />
      </g>
    </svg>
  );
}

const MENU_ICONS: Readonly<Record<MenuId, () => ReactNode>> = {
  audio: AudioLanguageIcon,
  subtitles: SubtitlesIcon,
  speed: SpeedIcon,
  quality: QualityIcon,
};

/**
 * One of §7's four menus. The popover *is* the shared `ui/Menu` — the search filter row
 * was the second caller `ui/index.ts` had been waiting for — and what stays here is only the two
 * things that are about being on a video: `variant="media"`, and `containEvents` so Escape closes the
 * menu instead of reaching the player container (which reads it as "leave fullscreen").
 */
function MenuButton({
  menu,
  openMenu,
  onOpen,
  onSelect,
}: {
  menu: PlayerMenu;
  /**
   * Which menu is open, rather than whether *this* one is — the difference matters since the narrow
   * bar's audio slot can hold a menu whose id is `subtitles` (a one-language video with captions,
   * merged down to the half that exists). Comparing against a literal per slot made that menu
   * unopenable: the trigger set `openMenu` to its own id and the slot was asking about another.
   */
  openMenu: MenuId | null;
  onOpen: (id: MenuId | null) => void;
  onSelect: (id: MenuId, itemId: string) => void;
}) {
  const Icon = MENU_ICONS[menu.id];
  return (
    <Menu
      menu={menu}
      open={openMenu === menu.id}
      variant="media"
      containEvents
      placement="top-end"
      showTriggerLabel={false}
      triggerIcon={<Icon />}
      onOpenChange={(next) => onOpen(next ? menu.id : null)}
      onSelect={(itemId) => onSelect(menu.id, itemId)}
    />
  );
}

// ---------------------------------------------------------------------------
// The bar
// ---------------------------------------------------------------------------

function Segment({ segment, marks }: { segment: TimelineSegment; marks: readonly SponsorBarMark[] }) {
  return (
    <div
      className={cx(styles.segment, segment.active && styles.segmentActive)}
      // `flex-grow: duration` — §7. A minimum of 1 keeps a zero-length chapter from collapsing to
      // nothing and swallowing its own gap.
      style={{ flexGrow: Math.max(segment.durationSec, 1) }}
      title={segment.title ?? undefined}
    >
      <div className={styles.buffered} style={{ transform: `scaleX(${segment.bufferedFraction})` }} />
      <div className={styles.played} style={{ transform: `scaleX(${segment.playedFraction})` }} />
      {/* SponsorBlock stripes, clipped to this cell — the colour is the reader's own setting, so it
          arrives as data (inline style), the storyboard crop's precedent. */}
      {marks.map((mark) => (
        <div
          key={mark.uuid}
          className={styles.sponsorMark}
          style={{
            left: `${mark.startFraction * 100}%`,
            width: `${(mark.endFraction - mark.startFraction) * 100}%`,
            backgroundColor: mark.color,
          }}
        />
      ))}
    </div>
  );
}

function ScrubBar({ binding }: { binding: WatchBinding }) {
  const words = useCopy().text.watch.chrome;
  const { transport, chrome, handlers, storyboard, video } = binding;
  const chapters = video?.chapters ?? [];
  const segments = barSegments(chapters, transport, chrome.scrubbingSec);
  const range = barRange(transport, chrome.scrubbingSec);
  const knobColor = barKnobColor(transport, chrome.scrubbingSec, binding.sponsor.segments);
  const hover =
    chrome.hoverFraction === null
      ? null
      : scrubHover(
          chrome.hoverFraction,
          transport,
          chapters,
          storyboard,
          PREVIEW_WIDTH_PX,
          binding.sponsor.segments,
        );

  const onMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width <= 0) return;
    handlers.hoverBar((event.clientX - box.left) / box.width);
  };

  return (
    <div className={styles.scrub} onPointerMove={onMove} onPointerLeave={() => handlers.hoverBar(null)}>
      <div className={styles.track}>
        {segments.map((segment) => (
          <Segment
            segment={segment}
            marks={barSponsorMarks(segment, binding.sponsor.segments)}
            key={`${segment.index}:${segment.startSec}`}
          />
        ))}
      </div>

      {hover === null ? null : (
        <div className={styles.tip} style={{ left: `${hover.fraction * 100}%` }}>
          {/* The segment's own line rides on top — above the picture when there is one, above the
              time when there is not. Never colour alone: the dot carries the reader's colour, the
              word carries the meaning. */}
          {hover.sponsor === null ? null : (
            <span className={styles.tipSponsor}>
              <span
                className={styles.tipSponsorDot}
                style={{ backgroundColor: hover.sponsor.color }}
                aria-hidden="true"
              />
              {hover.sponsor.name}
            </span>
          )}
          {hover.preview === null ? null : (
            <span
              className={styles.tipThumb}
              style={{ width: hover.preview.boxWidth, height: hover.preview.boxHeight }}
            >
              <span
                className={styles.tipTile}
                style={{
                  width: hover.preview.tileWidth,
                  height: hover.preview.tileHeight,
                  backgroundImage: `url(${hover.preview.imageUrl})`,
                  backgroundPosition: `-${hover.preview.offsetX}px -${hover.preview.offsetY}px`,
                  transform: `scale(${hover.preview.scale})`,
                }}
              />
            </span>
          )}
          {hover.chapterTitle === null ? null : (
            <span className={styles.tipChapter}>{hover.chapterTitle}</span>
          )}
          <span className={styles.tipTime}>{hover.timeText}</span>
        </div>
      )}

      {/*
        The native range stays. It is what carries the keyboard behaviour, the accessible name and the
        value announcement — none of which a row of painted divs has — while the segmented layer above
        provides the picture a range element cannot express.
      */}
      <input
        className={styles.input}
        // The knob wears the drawn segment's colour while the displayed playhead is inside one —
        // reader data like the stripes, so it travels inline; the stylesheet holds the fallback.
        // The cast is for the custom property, which `CSSProperties` cannot name.
        style={knobColor === null ? undefined : ({ '--sponsor-knob': knobColor } as CSSProperties)}
        type="range"
        min={range.min}
        max={range.max}
        step={range.step}
        // Every one of those four is `barRange`'s rather than written out here, and that is the point:
        // the element snaps its value to its step, so a playhead handed over raw is a number it cannot
        // hold — and React deciding "the user changed this" by comparing what it wrote with what the
        // element kept then made the element's own rounding look like a seek somebody asked for.
        value={range.value}
        onChange={(event) => handlers.scrubTo(Number(event.target.value))}
        onPointerDown={handlers.scrubStart}
        onPointerUp={handlers.scrubEnd}
        onPointerCancel={handlers.scrubCancel}
        aria-label={words.seek}
        aria-valuetext={words.position(transport.currentText, transport.durationText)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// The bar as a whole
// ---------------------------------------------------------------------------

/**
 * What the page around the bar has taken away from it. Every field is `true` on /watch, and /embed
 * is the only caller that sets any of them — two because a bar inside somebody's iframe has nothing
 * to act on, and one from the embed URL itself.
 */
export interface ChromeOptions {
  /** `false` on /embed (2026-08-02): the surface *is* the viewport, so theater is a no-op there. */
  readonly theater?: boolean;
  /**
   * Whether the page around this player has a chapter list to open. `false` on /embed, where the
   * bar still names the chapter and there is nothing behind the name to press it towards.
   */
  readonly chapterListing?: boolean;
  /** `false` for `/embed/ID?fs=0`, the embedder saying this frame does not go fullscreen. */
  readonly fullscreen?: boolean;
  /**
   * The widen control. `false` on /embed, whose box is the embedder's and whose surface opts out of
   * the watch page's geometry entirely (`surfaceClassName`), so there is no stage of ours to crop.
   */
  readonly widen?: boolean;
}

export function PlayerChrome({
  binding,
  options = {},
}: {
  binding: WatchBinding;
  options?: ChromeOptions;
}) {
  const {
    theater = true,
    chapterListing = true,
    fullscreen: fullscreenButton = true,
    widen = true,
  } = options;
  const words = useCopy().text.watch.chrome;
  const { transport, chrome, handlers, stage, video } = binding;
  const live = stage.kind === 'player' && stage.live;
  // Muted reads as zero on the slider — the level is remembered, but the bar states what is audible.
  const level = transport.muted ? 0 : transport.volume;
  const chapterTitle =
    transport.chapterIndex === null ? null : (video?.chapters[transport.chapterIndex]?.title ?? null);

  return (
    <div
      className={cx(styles.chrome, !chrome.visible && styles.hidden)}
      // Hidden chrome must not be reachable by Tab either — a focus ring on an invisible control is the
      // worst of both. `inert` also blocks the pointer, so a faded bar cannot be clicked.
      inert={!chrome.visible}
      /*
       * The chrome must not inherit the surface's tap gesture: a click on the play button is not also a
       * click on the video. **Both** ends of the gesture are stopped, not just the up — the surface
       * pairs them, so letting the `pointerdown` through would leave a down position with no matching
       * up, and the next real tap on the video would measure its movement from wherever a menu button
       * happened to be and be discarded as a drag.
       */
      onPointerDown={(event) => event.stopPropagation()}
      onPointerUp={(event) => event.stopPropagation()}
      /*
       * Which of the two surfaces the pointer is moving over is the bar's own fact to report, and the
       * auto-hide needs it: "the pointer has stopped" means *stopped watching the controls* over the
       * picture and *arrived at one* over the bar.
       *
       * `onPointerMove` as well as `onPointerEnter`, and not for redundancy: a hidden bar is `inert`,
       * so nothing here is hit-testable and no `pointerenter` can fire. The pointer arriving is seen by
       * the surface first, which is what reveals the bar — and the next move over it is then the only
       * thing that says the pointer is on it.
       *
       * The move is stopped for the same reason the tap is: it must not *also* be a move over the
       * picture, which is what the surface's own handler reads it as. Leaving is handed to that handler
       * on purpose — a `pointerleave` is a pointer that moved, and it moved off the bar.
       */
      onPointerEnter={handlers.chromeActivity}
      onPointerMove={(event) => {
        event.stopPropagation();
        handlers.chromeActivity();
      }}
      onPointerLeave={handlers.pointerActivity}
    >
      {live ? <div className={styles.liveSpacer} /> : <ScrubBar binding={binding} />}

      <div className={styles.transport}>
        <IconButton
          variant="media"
          onClick={handlers.togglePlay}
          label={transport.playing ? words.pause : words.play}
        >
          <PlayIcon playing={transport.playing} />
        </IconButton>

        {/* Gone below `md`: the picture's own double-tap zones are the skip control on a
            phone, and two buttons that duplicate a gesture are two buttons' worth of a bar that has
            to fit a quality menu and a fullscreen button on a 360 px line. */}
        <IconButton
          variant="media"
          className={styles.skipButton}
          onClick={() => handlers.seekBy(-SKIP_SEC)}
          label={words.back10}
          disabled={live}
        >
          <SkipIcon forward={false} />
        </IconButton>
        <IconButton
          variant="media"
          className={styles.skipButton}
          onClick={() => handlers.seekBy(SKIP_SEC)}
          label={words.forward10}
          disabled={live}
        >
          <SkipIcon forward />
        </IconButton>

        <div className={styles.volumeGroup}>
          <IconButton
            variant="media"
            onClick={handlers.toggleMute}
            label={transport.muted ? words.unmute : words.mute}
          >
            <MuteIcon muted={transport.muted} volume={transport.volume} />
          </IconButton>
          {/* The slider itself is `ui/VolumeSlider` since the minimized player became its second
              caller; what stays here is the collapse-on-hover, which is this bar's behaviour
              and not the control's. */}
          <VolumeSlider
            className={styles.volumeCollapsible}
            level={level}
            onChange={handlers.setVolume}
            label={words.volume}
          />
        </div>

        {/* Below `md` the playhead alone: the total is on the scrub bar's own tooltip and in
            the metadata below, and "0:42 / 10:35" is twice the width of the thing anyone reads. */}
        <span className={styles.time}>
          {live ? (
            <span className={styles.liveBadge}>{words.live}</span>
          ) : chrome.narrow ? (
            transport.currentText
          ) : (
            `${transport.currentText} / ${transport.durationText}`
          )}
        </span>

        {/*
            The chapter name presses through to the list (2026-08-25): it opens the chapter panel if
            it is hidden and scrolls that chapter's row into view, which is the act a reader who has
            just read the name off the bar is reaching for. A plain `<span>` where there is no list
            behind it — /embed — rather than a button that would do nothing.

            It needs no pointer handlers of its own: the bar around it already stops both gesture
            ends and keeps the auto-hide from taking a control away from a pointer that reached one.
        */}
        {chapterTitle === null ? null : chapterListing ? (
          <button
            type="button"
            className={cx(styles.chapterName, styles.chapterNameButton)}
            title={words.showChapter(chapterTitle)}
            aria-label={words.showChapter(chapterTitle)}
            onClick={handlers.revealChapters}
          >
            {chapterTitle}
          </button>
        ) : (
          <span className={styles.chapterName}>{chapterTitle}</span>
        )}

        <div className={styles.spacer} />

        {/* `subtitles` is `null` below `md`, where its rows ride in the audio control instead. */}
        {chrome.subtitles === null ? null : (
          <MenuButton
            menu={chrome.subtitles}
            openMenu={chrome.openMenu}
            onOpen={handlers.openMenu}
            onSelect={handlers.selectMenuItem}
          />
        )}
        {chrome.audio === null ? null : (
          <MenuButton
            menu={chrome.audio}
            openMenu={chrome.openMenu}
            onOpen={handlers.openMenu}
            onSelect={handlers.selectMenuItem}
          />
        )}
        <MenuButton
          menu={chrome.speed}
          openMenu={chrome.openMenu}
          onOpen={handlers.openMenu}
          onSelect={handlers.selectMenuItem}
        />
        <MenuButton
          menu={chrome.quality}
          openMenu={chrome.openMenu}
          onOpen={handlers.openMenu}
          onSelect={handlers.selectMenuItem}
        />

        {/* The widen control, left of the theater toggle and outliving it in fullscreen
            (2026-09-09). `stageCropAvailable` is a 16∶9 video in one of the two modes whose box can
            take a crop — theater or fullscreen — since a picture already wider than 16∶9 has no bars
            to take, and on the ordinary page the column's width is fixed, so filling a 21∶9 box would
            only make the video shorter. `widen` takes it off /embed, where the crop has no stage of
            ours to act on. */}
        {widen && binding.stageCropAvailable ? (
          <IconButton
            variant="media"
            className={styles.widenButton}
            onClick={handlers.toggleStageCrop}
            label={binding.stageCrop ? words.exitWiden : words.widen}
          >
            <WidenIcon active={binding.stageCrop} />
          </IconButton>
        ) : null}

        {/* Only where it does anything: below the wide breakpoint the player already spans the page,
            and a mode toggle that changes nothing is the "shown broken" §8 forbids — the same rule
            that removes it from /embed entirely (`ChromeOptions.theater`), where it would reshape
            the reader's /watch layout instead. **In fullscreen it is gone as well** (2026-09-09):
            the picture is the whole screen there, so the page arrangement underneath it is not
            something to be deciding, and the widen control takes its place on the line. */}
        {theater && !chrome.fullscreen ? (
          <IconButton
            variant="media"
            className={styles.theaterButton}
            onClick={handlers.toggleTheater}
            label={binding.layout.theater ? words.exitTheater : words.theater}
          >
            <TheaterIcon active={binding.layout.theater} />
          </IconButton>
        ) : null}

        {/* `fs=0` takes the button and nothing else: the keymap's `F` and the browser's own way in
            are the embedder's to allow through the iframe, not this bar's to police. */}
        {fullscreenButton ? (
          <IconButton
            variant="media"
            onClick={handlers.toggleFullscreen}
            label={chrome.fullscreen ? words.exitFullscreen : words.fullscreen}
          >
            <FullscreenIcon active={chrome.fullscreen} />
          </IconButton>
        ) : null}
      </div>
    </div>
  );
}
